import React, { Component } from "react"
import Helmet from "react-helmet"
import Link from "gatsby-link"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import {
  TERTIARY_TEXT,
  SECONDARY_TEXT,
  PRIMARY_TEXT,
  SMALLCHAT_SHADOW,
  SMALLCHAT_PURPLE,
  SMALLCHAT_BKG,
  SELECTED_TEXT,
} from "../components/consts/colors"
import withScreensize from "../components/withScreensize"

const TermsWrapper = styled.div`
  display: flex;
  width: 100%;
  background-color: ${SMALLCHAT_BKG};

  a {
    transition: 200ms ease all;
    color: ${SELECTED_TEXT};
  }

  a:hover {
    transform: scale(1.01);
    color: ${PRIMARY_TEXT};
  }
`
const TermsSidebar = styled.div`
  display: flex;
  flex-direction: column;
  position: fixed;
  height: 500px;
  width: 300px;
  margin-top: 50px;

  @media screen and (max-width: 980px) {
    width: 200px;
  }

  @media screen and (max-width: 768px) {
    display: none;
  }
`

const TermsLink = styled(Link)`margin: 20px 0px 0px 40px;`

const TermsContent = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 70%;
  background-color: white;
  border-radius: 16px;
  box-shadow: 0px 10px 30px ${SMALLCHAT_SHADOW};
  margin: 40px 0px 100px 0px;
  padding: 20px 40px;
  left: 300px;

  h1 {
    margin-bottom: 40px;
    margin-top: 40px;
    &:first-child {
      margin-top: 0px;
    }
  }
  h3 {
    margin-top: 20px;
    margin-bottom: 20px;
  }
  i {
    margin-top: 100px;
    margin-bottom: 40px;
  }

  @media screen and (max-width: 980px) {
    width: 65%;
    left: 200px;
  }
  @media screen and (max-width: 768px) {
    width: 80%;
    justify-content: center;
    margin: 0 auto;
    margin-top: 50px;
    margin-bottom: 100px;
    padding: 10px 30px;
  }

  @media screen and (max-width: 480px) {
    width: 90%;
    padding: 5px 20px;

    margin-bottom: 50px;
  }
`

function Terms({ screensize }) {
  const isMobile = screensize === "phone"
  const isTablet = screensize === "tablet"

  return (
    <div>
      <Helmet title="Terms & Privacy Policy" />
      <TermsWrapper>
        <TermsSidebar>
          <TermsLink to="/terms#terms">Terms of Service</TermsLink>
          <TermsLink to="/terms#privacy">Privacy Policy</TermsLink>
          <TermsLink to="/terms#gdpr">GDPR</TermsLink>
        </TermsSidebar>
        <TermsContent>
          <h1 id="terms">Terms of Use</h1>
          <p>
            By using Nicer LLC&rsquo;s Smallchat service, you are agreeing to be
            bound by these service Terms and Conditions of Use, all applicable
            laws and regulations, and agree that you are responsible for
            compliance with any applicable local laws. If you do not agree with
            any of these terms, you are prohibited from using or accessing this
            service. The materials contained in this service are protected by
            applicable copyright and trade mark law.
          </p>
          <h3>Use License</h3>
          <p>
            Permission is granted to temporarily download one copy of the
            materials (information or software) on Nicer LLC&rsquo;s Smallchat
            service for personal, non-commercial transitory viewing only. This
            is the grant of a license, not a transfer of title, and under this
            license you may not:
          </p>
          <ul>
            <li>modify or copy the materials;</li>
            <li>
              attempt to decompile or reverse engineer any software contained on
              Nicer LLC&rsquo;s Smallchat service;
            </li>
            <li>
              remove any copyright or other proprietary notations from the
              materials; or
            </li>
            <li>
              transfer the materials to another person or &ldquo;mirror&rdquo;
              the materials on any other server.
            </li>
          </ul>
          <p>
            This license shall automatically terminate if you violate any of
            these restrictions and may be terminated by Nicer LLC at any time.
          </p>
          <h3>Disclaimer</h3>
          <p>
            The materials on Nicer LLC&rsquo;s Smallchat service are provided
            &ldquo;as is&rdquo;. Nicer LLC makes no warranties, expressed or
            implied, and hereby disclaims and negates all other warranties,
            including without limitation, implied warranties or conditions of
            merchantability, fitness for a particular purpose, or
            non-infringement of intellectual property or other violation of
            rights. Further, Nicer LLC does not warrant or make any
            representations concerning the accuracy, likely results, or
            reliability of the use of the materials on its Smallchat service or
            otherwise relating to such materials or on any sites linked to this
            service.
          </p>
          <h3>Limitations</h3>
          <p>
            In no event shall Nicer LLC be liable for any damages (including,
            without limitation, damages for loss of data or profit, or due to
            business interruption,) arising out of the use or inability to use
            the materials on Nicer LLC&rsquo;s Smallchat service, even if Nicer
            LLC or a Nicer LLC authorized representative has been notified
            orally or in writing of the possibility of such damage. Because some
            jurisdictions do not allow limitations on implied warranties, or
            limitations of liability for consequential or incidental damages,
            these limitations may not apply to you.
          </p>
          <h3>Revisions and Errata</h3>
          <p>
            The materials appearing on Nicer LLC&rsquo;s Smallchat service could
            include technical, typographical, or photographic errors. Nicer LLC
            does not warrant that any of the materials on its Smallchat service
            are accurate, complete, or current. Nicer LLC may make changes to
            the materials contained on its Smallchat service at any time without
            notice. Nicer LLC does not, however, make any commitment to update
            the materials.
          </p>
          <h3>Content</h3>
          <p>
            Nicer LLC&rsquo;s Smallchat service allows you to send messages,
            text, files and other materials (together the &ldquo;Content&rdquo;)
            to others. You retain ownership of your Content, but by uploading it
            onto the Service, you are granting us a license to use, copy,
            reproduce, process, adapt, publish, transmit, host and display that
            Content for any purpose.
          </p>
          <p>
            We reserve the right to remove Content on the Service for any
            reason.
          </p>
          <h3>Refunds</h3>
          <p>
            Refunds must be requested within 7 days of the initial purchase. We
            will not process refunds for more than one transaction per account.
          </p>
          <h3>Service Terms of Use Modifications</h3>
          <p>
            Nicer LLC may revise these terms of use for its Smallchat service at
            any time without notice. By using this service you are agreeing to
            be bound by the then current version of these Terms and Conditions
            of Use.
          </p>
          <h3>Governing Law</h3>
          <p id="privacy">
            Any claim relating to Nicer LLC&rsquo;s Smallchat service shall be
            governed by the laws of the State of South Carolina without regard
            to its conflict of law provisions.
          </p>
          <h1>Privacy Policy</h1>
          <p>
            Your privacy and data are important to us. Our privacy policy is as
            follows:
          </p>
          <h3>Information we collect</h3>
          <p>
            Through the Smallchat Slack integration, we access and store
            information about your team and team&rsquo;s members for the purpose
            of displaying that information to visitors you chat with. That
            information includes but is not limited to full names and profile
            pictures. We also have access to and may store, but do not display
            to visitors, data available through the Slack OAuth scopes that
            Smallchat accesses. The data available through these scopes can be
            reviewed in Slack&rsquo;s API documentation.
          </p>
          <p>
            We may access and store email addresses from users of the Service.
            We do not sell those email addresses but may use them to send
            information about Smallchat, maintenance, changes to Smallchat, and
            new features.
          </p>
          <p>
            We process message exchanges between the Smallchat customer and
            end-user. This includes publicly available information on the
            end-user: browser name and version, operating device type, and
            activity time. We collect but do not display the end-user&rsquo;s
            public IP address, for security, and to provide the Smallchat
            customer a method to prevent further messages from the end-user. The
            public IP address is also used to guess the end-user&rsquo;s city
            and country location to assist the customer in helping the end-user.
          </p>
          <p>
            Any other data collected on a Smallchat customer&rsquo;s end-users
            is the responsibility of that Smallchat customer to manage. It is
            our responsibility to secure access to this data.
          </p>
          <h3>Information we share</h3>
          <p>
            Smallchat does not share any personal data, for advertising or any
            other purpose.
          </p>
          <h3>Privacy communication</h3>
          <p>
            We are committed to ensuring that the confidentiality of personal
            information is protected and maintained. Smallchat customers should
            include their own terms of use and privacy policy. We will make
            readily available to customers information about our policies and
            practices relating to the management of personal information.
          </p>
          <h3>Consent</h3>
          <p id="gdpr">
            Smallchat only processes customer data after receiving explicit
            consent via Slack authorization. We also process end-user-submitted
            data in message exchanges. Any data collected with Smallchat&rsquo;s
            Javascript API must be done lawfully and with the end-user&rsquo;s
            consent.
          </p>
          <h1>GDPR</h1>
          <p>
            We&rsquo;ve documented our steps to become GDPR compliant. For more
            information on the following steps, see&nbsp;<a
              href="https://ico.org.uk/media/1624219/preparing-for-the-gdpr-12-steps.pdf"
              rel="noreferrer nofollow noopener"
              target="_blank">
              https://ico.org.uk/media/1624219/preparing-for-the-gdpr-12-steps.pdf
            </a>
          </p>
          <h3>Awareness</h3>
          <p>
            All Nicer LLC employees working on Smallchat are aware of GDPR
            requirements and informed about our steps to be compliant.
          </p>
          <h3>Information we hold</h3>
          <p>
            Smallchat collects personal data solely for the purpose of providing
            the service, and only with the consent of the user. Smallchat
            processes the following types of personal data:
          </p>
          <ul>
            <li>Slack user first and last name</li>
            <li>Slack user email address</li>
            <li>Slack user profile</li>
            <li>Slack team profile</li>
            <li>Message exchanges between Slack user and end-user</li>
            <li>Activity date and time</li>
            <li>End-user name&nbsp;(if&nbsp;provided)</li>
            <li>End-user email address&nbsp;(if&nbsp;provided)</li>
            <li>
              End-user IP address&nbsp;(used&nbsp;for security, and to provide a
              method to prevent messages from an end-user)
            </li>
            <li>
              End-user device type&nbsp;(operating&nbsp;system and browser)
            </li>
            <li>
              End-user city and country&nbsp;(guessed&nbsp;from the IP address)
            </li>
          </ul>
          <h3>Individuals&rsquo; rights</h3>
          <p>Smallchat upholds our customers&rsquo; rights regarding GDPR:</p>
          <ul>
            <li>
              Right to be informed: we are transparent about how we use your
              data.
            </li>
            <li>
              Right of access: our users can request access to all their data
              from Smallchat
            </li>
            <li>
              Right of rectification: our users can request any personal data be
              rectified if it is inaccurate or incomplete.
            </li>
            <li>
              Right to restrict processing: we don&#39;t process the data of our
              customers or our customers&rsquo; end-users.
            </li>
            <li>
              Right of erasure: our users can request to have personal data
              erased.
            </li>
            <li>
              it&#39;s as simple as contacting us, we&#39;ll process all your
              erasure queries
            </li>
            <li>
              Right to data portability: our users can request an export of
              their personal data.
            </li>
            <li>
              Right to object: our users can contact us at any time with regards
              to this matter.
            </li>
            <li>
              Rights related to automation &amp; profiling: we do not use your
              data for automation or profiling.
            </li>
          </ul>
          <h3>Subject access requests</h3>
          <p>
            We will respond within 30 days to support requests about
            users&rsquo; rights.
          </p>
          <h3>Lawful basis for processing personal data</h3>
          <p>
            Smallchat obtains personal data from your Slack team after explicit
            consent when you add Smallchat to your Slack account. It is your
            responsibility as a Smallchat customer to ensure any data you
            otherwise collect through Smallchat messages is obtained lawfully.
          </p>
          <h3>Children</h3>
          <p>
            Smallchat requires a minimum age of 16. This is indirectly enforced
            due to our integration with Slack. You must have a Slack account in
            order to set up a Smallchat account, and Slack also requires a
            minimum age of 16. Children may interact as end-users by using the
            Smallchat messenger. In this case it is the Smallchat
            customer&rsquo;s responsibility to uphold GDPR regulations regarding
            children.
          </p>
          <h3>Data breaches</h3>
          <p>
            Smallchat takes seriously the security and privacy of your data. In
            the event of a data breach, we will notify our users and pertinent
            sub-processors immediately after the discovering and fixing the
            breach.
          </p>
          <h3>Data Protection Officers</h3>
          <p>
            Nicer LLC has appointed a data protection officer for Smallchat:
          </p>
          <ul>
            <li>Name: Robert Gonzales</li>
            <li>Role: Smallchat Developer</li>
            <li>Contact: support@small.chat</li>
          </ul>
          <h3>International</h3>
          <p>
            Nicer LLC is based in the United States, so rules regarding
            operation within EU member states do not apply.
          </p>
          <br />
          <p>
            If you notice anything missing, please notify us and we will
            immediately take steps to rectify this documentation or compliance.
          </p>
        </TermsContent>
      </TermsWrapper>
    </div>
  )
}

export default withScreensize(Terms)



// WEBPACK FOOTER //
// ./src/pages/terms.js